import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import sha256 from "crypto-js/sha256";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import { PortalContext } from "App";
import {
  checkNonMoneyDataForTable,
  trimObject,
  dateTimeFormatter,
} from "utils/utils";
import { queryAgent, changePassword } from "./model";
import { isAllInputValid } from "utils/formValidation";
import s from "./Agents.module.scss";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import ColumnFilter from "components/ColumnFilter/ColumnFilter";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import moment from "moment";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import PayfacPrefixSearchFilter from "components/PayfacPrefixSearchField/PayfacPrefixSearchFilter";
import {
  usePayfacPrefixColumn,
} from "components/PayfacPrefixSearchField/payfacPrefixColumn";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import merchantManagement from "routes/merchantManagement";

import ChangePasswordStrongDialog from "pages/merchant-management/components/change-password-strong/ChangePasswordStrongDialog";
import useRefresh from "utils/useRefresh/useRefresh";

const PAGE_LOCALE_KEY = "page.agents.agentslist.";
const DIALOG_LOCALE_KEY = "page.dialogs.";

const INPUT_FIELD_INIT_STATE = {
  isoAgentId: "",
  payfacPrefix: "",
  firstName: "",
  lastName: "",
  email: "",
};

const INPUT_FIELD_ID_OVERRIDE_STATE = {
  payfacPrefix: "",
  firstName: "",
  lastName: "",
  email: "",
};

const ERROR_INIT_STATE = {
  newPassword: false,
};

const CHANGEPASSWORD_INPUT_INIT_STATE = {
  id: "",
  newPassword: "",
};

const Agents = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { currentPath } = useContext(PortalContext);
  const gridId = "AgentListGrid";

  const [searching, setSearching] = useState(false);
  const [datasource, setDatasource] = useState({
    data: [],
    page: 0,
    total: 0,
  });
  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [searchFieldInput, setSearchFieldInput] = useState(
    INPUT_FIELD_INIT_STATE,
  );

  const [focus, setFocus] = useState({});
  const [error, setError] = useState(ERROR_INIT_STATE);
  const [isIdInput, setIsIdInput] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const [columnDisplayDialog, setColumnDisplayDialog] = useState({
    open: false,
    position: 0,
  });
  const [columnsArray, setColumnsArray] = useState([]);

  const [changePasswordDialogData, setChangePasswordDialogData] = useState(
    CHANGEPASSWORD_INPUT_INIT_STATE,
  );
  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] =
    useState(false);

  const [queryAgentLoading, setQueryAgentLoading] = useState(true);
  const payfacPrefixColumn = usePayfacPrefixColumn();
  const initialFetchDoneRef = useRef(false);
  const isLoading = queryAgentLoading;

  useRefresh({
    pagePath: path,
    preventRefresh: isLoading,
    redirectFn,
    effectFn: () => handleSearch(),
  });

  useEffect(() => {
    if (searchFieldInput.isoAgentId !== "" && !isIdInput) {
      setIsIdInput(true);
      setSearchFieldInput({
        ...searchFieldInput,
        ...INPUT_FIELD_ID_OVERRIDE_STATE,
      });
    } else if (searchFieldInput.isoAgentId === "" && isIdInput) {
      setIsIdInput(false);
      setSearchFieldInput(INPUT_FIELD_INIT_STATE);
    }
  }, [searchFieldInput.isoAgentId]);

  useEffect(() => {
    let isOutdated = false;
    const getIsOutdated = () => isOutdated;

    if (currentPath === path && !initialFetchDoneRef.current) {
      initialFetchDoneRef.current = true;
      initialFetchList(getIsOutdated);
    }

    return () => {
      isOutdated = true;
    };
  }, [currentPath, path]);

  const initialFetchList = async (getIsOutdated) => {
    setQueryAgentLoading(true);
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
    };
    const apiResponse = await queryAgent(payload);
    if (getIsOutdated && getIsOutdated()) return;

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            setDatasource(responseBody);
            setCurrentPage(responseBody.page);
            setPageSize(responseBody.size);
            pageSizeInfoRef.current.currentpage = responseBody.page;
            pageSizeInfoRef.current.pagesize = responseBody.size;
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
              "error",
            );
          }
        }
      }
    }

    setSearch(false);
    setQueryAgentLoading(false);
  };

  const resetInputField = () => {
    setSearchFieldInput(INPUT_FIELD_INIT_STATE);
  };

  const resetError = () => {
    setError(ERROR_INIT_STATE);
  };

  const handleReset = () => {
    setIsIdInput(false);
    resetInputField();
    resetError();
    initialFetchList();
  };

  const handleSearch = async (action) => {
    if (action === "search") {
      let isSearchValid = true;
      if (searchFieldInput.startDate && !searchFieldInput.endDate) {
        setError((error) => ({ ...error, endDate: true }));
        isSearchValid = false;
      }
      if (searchFieldInput.endDate && !searchFieldInput.startDate) {
        setError((error) => ({ ...error, startDate: true }));
        isSearchValid = false;
      }
      if (searchFieldInput.startDate > searchFieldInput.endDate) {
        setError((error) => ({ ...error, invalidDateRange: true }));
        isSearchValid = false;
      }
      const startDate = moment(searchFieldInput.startDate).startOf("day");
      const endDate = moment(searchFieldInput.endDate).startOf("day");
      if (endDate.diff(startDate, "month", true) > 6) {
        setError((error) => ({ ...error, tooLargeDateRange: true }));
        isSearchValid = false;
      }

      if (!isSearchValid) return;
      const allValid = isAllInputValid(error.email);
      if (!allValid) {
        return;
      }
      resetError();
      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    setSearching(true);

    const params = trimObject({ ...searchFieldInput });
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    const apiResponse = await queryAgent(payload);

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            if (responseBody.data.length === 0) {
              snackbar(t("general.message.no.result"), "warning");
            }
            setDatasource(responseBody);
            setCurrentPage(responseBody.page);
            setPageSize(responseBody.size);
            pageSizeInfoRef.current.currentpage = responseBody.page;
            pageSizeInfoRef.current.pagesize = responseBody.size;
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
              "error",
            );
            setDatasource({
              total: 0,
              page: 0,
              size: 10,
              data: [],
            });
          }
        }
      }
    }

    setSearch(true);
    setSearching(false);
  };

  // ===================== search field function start ===================== //

  const textInputOnChange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };

  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };

  const textInputOnBlur = (field, callback) => {
    if (searchFieldInput[field] && callback) {
      const isValidValue = callback(searchFieldInput[field]);
      if (!isValidValue) {
        setError({ ...error, [field]: error });
      }
    }
    setFocus({ [field]: false });
  };

  const isInputDisabled = (field) => {
    if (field !== "isoAgentId" && searchFieldInput.isoAgentId !== "")
      return true;
  };

  // ===================== search field function end ===================== //

  // ===================== column display function ===================== //

  const openColumnDisplay = (e) => {
    const position = e.target.getBoundingClientRect().top;
    setColumnDisplayDialog({
      open: true,
      position: position,
    });
    return;
  };

  const closeColumnDisplay = () => {
    setColumnDisplayDialog({
      ...columnDisplayDialog,
      open: false,
    });
    return;
  };

  // ===================== column display function ===================== //
  // ===================== change password dialog function ===================== //

  const openChangePasswordDialog = (data) => {
    setChangePasswordDialogData({
      ...changePasswordDialogData,
      id: data.email,
    });
    setIsChangePasswordDialogOpen(true);
  };

  const closeChangePasswordDialog = () => {
    restoreChangePasswordDialog();
    setIsChangePasswordDialogOpen(false);
  };

  const restoreChangePasswordDialog = () => {
    setChangePasswordDialogData(CHANGEPASSWORD_INPUT_INIT_STATE);
  };

  const updateChangePasswordPayload = (fieldInput) => {
    setChangePasswordDialogData({
      ...changePasswordDialogData,
      newPassword: fieldInput.newPassword,
    });
  };

  const updateChangePasswordResultCallback = async () => {
    if (changePasswordDialogData.newPassword == "") {
      return;
    }
    setSubmitting(true);

    const payload = {
      email: changePasswordDialogData.id,
      newPassword: sha256(changePasswordDialogData.newPassword).toString(),
    };

    const apiResponse = await changePassword(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t(PAGE_LOCALE_KEY + "fetchFail"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode !== "0000") {
            switch (responseBody.responseCode) {
              case "4313":
              case "4314":
              case "5999":
                snackbar(
                  t(
                    DIALOG_LOCALE_KEY +
                      `changePassword.msgFailed.${responseBody.responseCode}`,
                  ),
                  "error",
                );
                break;
              default:
                snackbar(
                  t(DIALOG_LOCALE_KEY + "changePassword.msgFailed") +
                    (responseBody.message ? ` (${responseBody.message})` : ""),
                  "error",
                );
            }
          } else {
            snackbar(
              t(DIALOG_LOCALE_KEY + "changePassword.msgSuccess"),
              "success",
            );
          }
        }
      }
    }

    setSubmitting(false);
    closeChangePasswordDialog();
    handleSearch();
  };

  const ChangePasswordDialogContent = () => {
    return (
      <ChangePasswordStrongDialog
        userId={changePasswordDialogData.email}
        updatePayload={updateChangePasswordPayload}
        onSubmit={
          !submitting ? updateChangePasswordResultCallback : undefined
        }
      />
    );
  };
  // ===================== change password dialog function end ===================== //

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const navigateToDetail = (data) => {
    const path = "/merchant-management/agents/agents-detail";
    const payload = {
      name:
        merchantManagement
          .find((tab) => tab.tabName === "Agents")
          .page.find((page) => page.path === path).name + data.isoAgentId,
      path: path,
      state: {
        isoAgentId: data.isoAgentId,
        email: data.email,
        status: data.status,
      },
    };
    redirectFn("redirectPage", payload);
  };

  const columns = [
    {
      key: "isoAgentId",
      title: t(PAGE_LOCALE_KEY + "columnTitle.isoAgentId"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    ...payfacPrefixColumn,
    {
      key: "firstName",
      title: t(PAGE_LOCALE_KEY + "columnTitle.firstName"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "lastName",
      title: t(PAGE_LOCALE_KEY + "columnTitle.lastName"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "email",
      title: t(PAGE_LOCALE_KEY + "columnTitle.email"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "role",
      title: t(PAGE_LOCALE_KEY + "columnTitle.role"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "createdDate",
      title: t(PAGE_LOCALE_KEY + "columnTitle.date"),
      sorter: false,
      render: (data, key) => {
        return dateTimeFormatter(checkNonMoneyDataForTable(data, key));
      },
    },
    {
      key: "status",
      title: t(PAGE_LOCALE_KEY + "columnTitle.status"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      key: "action",
      title: t(PAGE_LOCALE_KEY + "columnTitle.action"),
      sorter: false,
      render: (data) => {
        const { id } = data;

        return (
          <div className={s.actionBtnContainer}>
            <ButtonMAS variant="text-only" overlayLoading onClick={() => openChangePasswordDialog(data)}>
              {t(PAGE_LOCALE_KEY + "columnTitle.action2")}
            </ButtonMAS>

            <ButtonMAS
              variant="text-only"
              overlayLoading
              disabled={data.status === "TERMINATED"}
              onClick={() => {
                const path =
                  "/merchant-management/merchants/add-offline-merchant";
                const payload = {
                  name: merchantManagement
                    .find((tab) => tab.tabName === "Merchants")
                    .page.find((page) => page.path === path).name,
                  path: path,
                  state: {
                    id: id,
                    isoAgentEmail: data.email,
                    isoAgentId: data.isoAgentId,
                    firstName: data.firstName,
                    lastName: data.lastName
                  },
                };

                redirectFn("redirectPage", payload);
              }}
            >
              {t(PAGE_LOCALE_KEY + "columnTitle.action3")}
            </ButtonMAS>
          </div>
        );
      },
    },
  ].map((column, index) => ({ ...column, index: index }));

  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 30, 40],
    };
  }, [currentPage, pageSize]);

  const searchField = () => {
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId}>
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.isoAgentId.label")}
            value={searchFieldInput.isoAgentId}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "isoAgentId")}
            onfocusFn={() => textInputOnFocus("isoAgentId")}
            onblurFn={() => textInputOnBlur("isoAgentId")}
            sxParams={{ textFocus: focus.isoAgentId }}
            disabled={isInputDisabled("isoAgentId")}
          />
          <PayfacPrefixSearchFilter
            searchFieldInput={searchFieldInput}
            setSearchFieldInput={setSearchFieldInput}
            disabled={isInputDisabled("payfacPrefix")}
          />

          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.firstName.label")}
            value={searchFieldInput.firstName}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "firstName")}
            onfocusFn={() => textInputOnFocus("firstName")}
            onblurFn={() => textInputOnBlur("firstName")}
            sxParams={{ textFocus: focus.firstName }}
            disabled={isInputDisabled("firstName")}
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.email.label")}
            value={searchFieldInput.email}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnChange(e, "email")}
            onfocusFn={() => textInputOnFocus("email")}
            onblurFn={() => textInputOnBlur("email")}
            sxParams={{ textFocus: focus.email }}
            disabled={isInputDisabled("email")}
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "search.lastName.label")}
            value={searchFieldInput.lastName}
            onchangeFn={(e) => textInputOnChange(e, "lastName")}
            onfocusFn={() => textInputOnFocus("lastName")}
            onblurFn={() => textInputOnBlur("lastName")}
            sxParams={{ textFocus: focus.lastName }}
            disabled={isInputDisabled("lastName")}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={isLoading || searching}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <FunctionalButtonBar
          loading={isLoading}
          filterStatus={columnDisplayDialog}
          refreshBtn
          handleRefresh={handleSearch}
          columnDisplayBtn
          handleColumnDisplay={openColumnDisplay}
          columnDisplayProps={{
            columns,
            columnsArray,
            setColumnsArray,
            rememberPreference: "agent-list",
          }}
        />
        <div
          className={[s.datatableContainer, isLoading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {isLoading ? (
            <Spin />
          ) : (
            // TODO column sorting
            <Datatable
              navigateToDetail={navigateToDetail}
              columns={columns}
              columnsPreference={columnsArray}
              datasource={datasource.data}
              total={datasource.total}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
            />
          )}
          <ColumnFilter
            columnDisplayDialog={columnDisplayDialog}
            dialogOncloseFn={closeColumnDisplay}
            columns={columns}
            columnsArray={columnsArray}
            rememberPreference="agent-list"
            setColumnsArray={setColumnsArray}
          />
          {searching && <Spin type={"search"} />}

          <SimpleDialog
            open={isChangePasswordDialogOpen}
            dialogOncloseFn={closeChangePasswordDialog}
            title={t(DIALOG_LOCALE_KEY + "changePassword.title")}
            content={ChangePasswordDialogContent()}
            positiveAction={t(DIALOG_LOCALE_KEY + "btnSubmit")}
            negativeAction={t(DIALOG_LOCALE_KEY + "btnCancel")}
            negativeActionDisabled={submitting}
            positiveActionLoading={submitting}
            confirmFn={
              !submitting ? updateChangePasswordResultCallback : () => {}
            }
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(Agents);
